import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Plus, Calendar, Paperclip, MessageSquare, Trash2, ClipboardList } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { projectBoardService } from '../../api/boardApi';
import { useActivityLog } from '../../../../contexts/ActivityLogContext';
import TaskModal from './TaskModal';
import MilestoneModal from './MilestoneModal';
import TaskReviewModal from './TaskReviewModal';
import TaskDetailsModal from './TaskDetailsModal';

// ─── StrictMode DnD fix ───────────────────────────────────────────────────────
const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setEnabled(true));
    return () => { cancelAnimationFrame(frame); setEnabled(false); };
  }, []);
  if (!enabled) return null;
  return <Droppable {...props}>{children}</Droppable>;
};

// ─── Column config ────────────────────────────────────────────────────────────
const COLUMNS = {
  todo:       { label: 'To Do',       accent: '#37559e', dot: 'bg-[#37559e]', count: 'bg-blue-100 text-blue-700',   drag: 'bg-blue-50/60'  },
  inProgress: { label: 'In Progress', accent: '#1d4ed8', dot: 'bg-blue-600',  count: 'bg-indigo-100 text-indigo-700', drag: 'bg-indigo-50/60'},
  review:     { label: 'Review',      accent: '#d97706', dot: 'bg-amber-500', count: 'bg-amber-100 text-amber-700',  drag: 'bg-amber-50/60' },
  done:       { label: 'Done',        accent: '#15803d', dot: 'bg-green-600', count: 'bg-green-100 text-green-700',  drag: 'bg-green-50/60' },
  trash:      { label: 'Trash',       accent: '#9ca3af', dot: 'bg-gray-400',  count: 'bg-gray-100 text-gray-600',   drag: 'bg-gray-50/60'  },
};
const COLUMN_ORDER = ['todo', 'inProgress', 'review', 'done'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const AVATAR_PALETTE = ['#37559e','#bf6f50','#15803d','#b45309','#1d4ed8','#7c3aed','#0891b2','#be123c'];
const getInitials = (name) =>
  name ? name.trim().split(/\s+/).map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?';
const getAvatarColor = (name) =>
  AVATAR_PALETTE[(name?.charCodeAt(0) ?? 0) % AVATAR_PALETTE.length];

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};
const isOverdue = (dueDate) => {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
};
const isDueSoon = (dueDate) => {
  if (!dueDate) return false;
  const days = Math.ceil((new Date(dueDate) - new Date()) / 86400000);
  return days >= 0 && days <= 3;
};

const normalizeTaskList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.tasks)) return payload.tasks;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const EmptyColumn = ({ columnId }) => (
  <div className="flex flex-col items-center justify-center py-8 text-center">
    <ClipboardList size={28} className="text-gray-300 mb-2" />
    <p className="text-xs text-gray-400 font-medium">
      {columnId === 'todo' ? 'No tasks yet' :
       columnId === 'done' ? 'Nothing completed yet' :
       columnId === 'trash' ? 'Trash is empty' :
       'Nothing here'}
    </p>
  </div>
);

const AssigneeAvatars = ({ assignee }) => {
  const list = Object.values(assignee || {}).filter(a => a?.name);
  if (list.length === 0) return null;
  const visible = list.slice(0, 3);
  const overflow = list.length - 3;
  return (
    <div className="flex -space-x-1.5">
      {visible.map((a, i) => (
        <div
          key={i}
          title={a.name}
          className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold ring-1 ring-white select-none"
          style={{ backgroundColor: getAvatarColor(a.name) }}
        >
          {getInitials(a.name)}
        </div>
      ))}
      {overflow > 0 && (
        <div className="w-5 h-5 rounded-full flex items-center justify-center bg-gray-200 text-gray-600 text-[9px] font-bold ring-1 ring-white">
          +{overflow}
        </div>
      )}
    </div>
  );
};

const TaskCard = ({ task, index, onClick }) => {
  const draggableId = `task-${String(task.id)}`;
  const cfg = COLUMNS[task.status] || COLUMNS.todo;
  const overdue = isOverdue(task.dueDate);
  const soon = !overdue && isDueSoon(task.dueDate);

  return (
    <Draggable draggableId={draggableId} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            borderLeft: `3px solid ${cfg.accent}`,
            ...provided.draggableProps.style,
          }}
          className={`bg-white rounded-lg border border-gray-200 p-3 cursor-pointer select-none
            transition-all duration-150
            ${snapshot.isDragging
              ? 'shadow-xl opacity-95 scale-[1.02]'
              : 'shadow-sm hover:shadow-md hover:-translate-y-0.5'
            }`}
          onClick={() => !snapshot.isDragging && onClick(task)}
        >
          {/* Title */}
          <h4 className="text-sm font-semibold text-gray-900 leading-snug break-words">
            {task.title}
          </h4>

          {/* Description */}
          {task.description && (
            <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">
              {task.description}
            </p>
          )}

          {/* Review status chip */}
          {task.status === 'review' && task.review?.status && (
            <div className={`inline-flex items-center gap-1 mt-2 px-1.5 py-0.5 rounded text-[11px] font-medium
              ${task.review.status === 'approved'           ? 'bg-green-50 text-green-700' :
                task.review.status === 'changes_requested'  ? 'bg-red-50 text-red-700'   :
                'bg-amber-50 text-amber-700'}`}
            >
              <MessageSquare size={10} />
              {task.review.status === 'approved' ? 'Approved' :
               task.review.status === 'changes_requested' ? 'Changes Requested' :
               'Under Review'}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-gray-100 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {task.dueDate && (
                <span className={`flex items-center gap-1 text-[11px] shrink-0 ${
                  overdue ? 'text-red-600 font-medium' :
                  soon    ? 'text-amber-600' :
                            'text-gray-500'
                }`}>
                  <Calendar size={11} />
                  {formatDate(task.dueDate)}
                </span>
              )}
              {task.evidence?.length > 0 && (
                <span className="flex items-center gap-0.5 text-[11px] text-gray-400 shrink-0">
                  <Paperclip size={11} />
                  {task.evidence.length}
                </span>
              )}
            </div>
            <AssigneeAvatars assignee={task.assignee} />
          </div>
        </div>
      )}
    </Draggable>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const KanbanBoard = ({ projectId, currentUser, onMilestoneUpdate }) => {
  const { logTaskActivity, logMilestoneActivity } = useActivityLog();
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [selectedMilestoneId, setSelectedMilestoneId] = useState('all');
  const [project, setProject] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isTaskDetailsModalOpen, setIsTaskDetailsModalOpen] = useState(false);
  const [isTaskReviewModalOpen, setIsTaskReviewModalOpen] = useState(false);
  const [isMilestoneModalOpen, setIsMilestoneModalOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [showTrash, setShowTrash] = useState(false);

  const projectTeamMembers = project?.members || project?.team || [];

  // ── Data loading ────────────────────────────────────────────────────────────
  const loadProject = useCallback(async () => {
    try {
      setProject(await projectBoardService.getProject(projectId));
    } catch { toast.error('Failed to load project'); }
  }, [projectId]);

  const loadMilestones = useCallback(async () => {
    try {
      setMilestones(await projectBoardService.getProjectMilestones(projectId));
    } catch { toast.error('Failed to load milestones'); }
  }, [projectId]);

  const loadAllTasks = useCallback(async () => {
    try {
      const raw = await projectBoardService.getProjectTasks(projectId);
      const data = normalizeTaskList(raw);
      setAllTasks(data);
      setTasks(selectedMilestoneId === 'all'
        ? data
        : data.filter(t => t.milestoneId === selectedMilestoneId));
    } catch { toast.error('Failed to load tasks'); }
  }, [projectId, selectedMilestoneId]);

  useEffect(() => { loadProject(); loadMilestones(); loadAllTasks(); },
    [loadProject, loadMilestones, loadAllTasks]);

  useEffect(() => {
    if (allTasks.length > 0) {
      setTasks(selectedMilestoneId === 'all'
        ? allTasks
        : allTasks.filter(t => t.milestoneId === selectedMilestoneId));
    }
  }, [selectedMilestoneId, allTasks]);

  // ── Columns ─────────────────────────────────────────────────────────────────
  const columns = useMemo(() => ({
    todo:       tasks.filter(t => t.status === 'todo'),
    inProgress: tasks.filter(t => t.status === 'inProgress'),
    review:     tasks.filter(t => t.status === 'review'),
    done:       tasks.filter(t => t.status === 'done'),
    trash:      tasks.filter(t => t.status === 'trash'),
  }), [tasks]);

  const hasTrash = columns.trash.length > 0;

  // ── Drag & drop ─────────────────────────────────────────────────────────────
  const onDragEnd = async ({ source, destination, draggableId }) => {
    if (!destination) return;
    const src = String(source.droppableId);
    const dest = String(destination.droppableId);
    if (src === dest && source.index === destination.index) return;

    const rawId = draggableId.startsWith('task-') ? draggableId.slice(5) : draggableId;
    const task = allTasks.find(t => String(t.id) === rawId);
    if (!task) return;

    const updatedTask = {
      ...task,
      status: dest,
      review: dest === 'review'
        ? { startedAt: new Date().toISOString(), status: 'pending', reviewers: [], comments: task.review?.comments || [] }
        : task.review,
    };

    const prevAll = allTasks;
    const prevTasks = tasks;
    setAllTasks(prev => prev.map(t => String(t.id) === rawId ? updatedTask : t));
    setTasks(prev => prev.map(t => String(t.id) === rawId ? updatedTask : t));

    try {
      await projectBoardService.updateTaskStatus(projectId, task.id, dest);
      logTaskActivity(dest === 'done' ? 'complete' : 'update', updatedTask, projectId);
    } catch {
      toast.error('Failed to move task');
      setAllTasks(prevAll);
      setTasks(prevTasks);
    }
  };

  // ── Task handlers ───────────────────────────────────────────────────────────
  const handleCreateTask = () => {
    if (selectedMilestoneId === 'all') {
      toast.error('Select a milestone first to add a task');
      return;
    }
    setCurrentTask(null);
    setIsTaskModalOpen(true);
  };

  const handleTaskClick = (task) => {
    setCurrentTask(task);
    if (task.status === 'review') setIsTaskReviewModalOpen(true);
    else setIsTaskDetailsModalOpen(true);
  };

  const handleSaveTask = async (taskData) => {
    try {
      if (currentTask) {
        const updated = await projectBoardService.updateTask(projectId, currentTask.id, {
          ...taskData, milestoneId: currentTask.milestoneId, updatedAt: new Date().toISOString(),
        });
        await logTaskActivity('update', updated, projectId);
        setAllTasks(p => p.map(t => t.id === currentTask.id ? updated : t));
        setTasks(p => p.map(t => t.id === currentTask.id ? updated : t));
      } else {
        const created = await projectBoardService.createTask(projectId, {
          ...taskData,
          status: 'todo',
          milestoneId: selectedMilestoneId,
          isDisposed: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        await logTaskActivity('create', created, projectId);
        setAllTasks(p => [...p, created]);
        if (selectedMilestoneId === 'all' || created.milestoneId === selectedMilestoneId) {
          setTasks(p => [...p, created]);
        }
      }
      setIsTaskModalOpen(false);
      setCurrentTask(null);
      toast.success(currentTask ? 'Task updated' : 'Task created');
    } catch {
      toast.error('Failed to save task');
    }
  };

  const handleClearTrash = async () => {
    if (!window.confirm('Permanently delete all trash tasks?')) return;
    try {
      await Promise.all(columns.trash.map(t => projectBoardService.deleteProjectTask(projectId, t.id)));
      setAllTasks(p => p.filter(t => t.status !== 'trash'));
      setTasks(p => p.filter(t => t.status !== 'trash'));
      toast.success('Trash cleared');
    } catch {
      toast.error('Failed to clear trash');
    }
  };

  const handleCreateMilestone = async (milestoneData) => {
    try {
      const ts = new Date().toISOString();
      const created = await projectBoardService.createMilestone(projectId, {
        ...milestoneData, createdAt: ts, updatedAt: ts, progress: 0, status: 'active',
        overseer: {
          email: projectTeamMembers[0]?.email || '',
          id:    projectTeamMembers[0]?.id    || '',
          name:  projectTeamMembers[0]?.name  || '',
          role:  projectTeamMembers[0]?.role  || '',
        },
      });
      await logMilestoneActivity('create', created, projectId);
      setMilestones(p => [...p, created]);
      setIsMilestoneModalOpen(false);
      toast.success('Milestone created');
      if (typeof onMilestoneUpdate === 'function') onMilestoneUpdate();
    } catch {
      toast.error('Failed to create milestone');
    }
  };

  const handleReviewAction = async (updatedTaskData) => {
    try {
      const updated = await projectBoardService.updateTask(projectId, currentTask.id, updatedTaskData);
      setAllTasks(p => p.map(t => t.id === currentTask.id ? updated : t));
      setTasks(p => p.map(t => t.id === currentTask.id ? updated : t));
      setIsTaskReviewModalOpen(false);
      setCurrentTask(null);
      toast.success('Review submitted');
    } catch {
      toast.error('Failed to submit review');
    }
  };

  // ── Milestone progress helpers ───────────────────────────────────────────────
  const getMilestoneProgress = useCallback((milestoneId) => {
    const mt = allTasks.filter(t => t.milestoneId === milestoneId && t.status !== 'trash');
    if (mt.length === 0) return 0;
    return Math.round(mt.filter(t => t.status === 'done').length / mt.length * 100);
  }, [allTasks]);

  const overallProgress = useMemo(() => {
    const nt = allTasks.filter(t => t.status !== 'trash');
    if (nt.length === 0) return 0;
    return Math.round(nt.filter(t => t.status === 'done').length / nt.length * 100);
  }, [allTasks]);

  // ── Column renderer ──────────────────────────────────────────────────────────
  const renderColumn = (columnId) => {
    const cfg = COLUMNS[columnId];
    const colTasks = columns[columnId] || [];
    return (
      <div key={columnId} className="flex flex-col flex-shrink-0 w-64 sm:w-72 rounded-xl border border-gray-200 bg-gray-50/70 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2.5 bg-white border-b border-gray-200">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
            <span className="text-sm font-semibold text-gray-800">{cfg.label}</span>
            <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${cfg.count}`}>
              {colTasks.length}
            </span>
          </div>
          {columnId === 'todo' && selectedMilestoneId !== 'all' && (
            <button
              onClick={handleCreateTask}
              title="Add task"
              className="p-1 rounded-md text-gray-400 hover:text-[#37559e] hover:bg-blue-50 transition-colors"
            >
              <Plus size={15} />
            </button>
          )}
        </div>

        {/* Drop zone */}
        <StrictModeDroppable droppableId={columnId} type="TASK">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`flex-1 p-2 space-y-2 min-h-[220px] transition-colors duration-150 overflow-y-auto
                ${snapshot.isDraggingOver ? cfg.drag : ''}`}
            >
              {colTasks.length === 0 && !snapshot.isDraggingOver && (
                <EmptyColumn columnId={columnId} />
              )}
              {colTasks.map((task, idx) => (
                <TaskCard
                  key={`task-${String(task.id)}`}
                  task={task}
                  index={idx}
                  onClick={handleTaskClick}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </StrictModeDroppable>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">

      {/* ── Milestone tabs ────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto hide-scrollbar">
          {/* All tab */}
          <button
            onClick={() => setSelectedMilestoneId('all')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all shrink-0
              ${selectedMilestoneId === 'all'
                ? 'bg-[#37559e] text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            All Tasks
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold
              ${selectedMilestoneId === 'all' ? 'bg-white/25 text-white' : 'bg-white text-gray-500 shadow-sm'}`}>
              {allTasks.filter(t => t.status !== 'trash').length}
            </span>
          </button>

          {/* Milestone tabs */}
          {milestones.map(ms => {
            const progress = getMilestoneProgress(ms.id);
            const active = selectedMilestoneId === ms.id;
            return (
              <button
                key={ms.id}
                onClick={() => setSelectedMilestoneId(ms.id)}
                className={`flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all shrink-0
                  ${active
                    ? 'bg-[#37559e] text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                <span className="max-w-[120px] truncate">{ms.title}</span>
                <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold shrink-0
                  ${active ? 'bg-white/25 text-white' : 'bg-white text-gray-500 shadow-sm'}`}>
                  <span
                    className="inline-block w-8 h-1 rounded-full overflow-hidden"
                    style={{ backgroundColor: active ? 'rgba(255,255,255,0.3)' : '#e5e7eb' }}
                  >
                    <span
                      className="block h-full rounded-full"
                      style={{
                        width: `${progress}%`,
                        backgroundColor: active ? 'white' : '#37559e',
                      }}
                    />
                  </span>
                  {progress}%
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Toolbar ───────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200 gap-2">
        <div className="flex items-center gap-2">
          {selectedMilestoneId !== 'all' && (
            <button
              onClick={handleCreateTask}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#37559e] text-white rounded-lg text-xs font-semibold hover:bg-[#2d4785] transition-colors shadow-sm"
            >
              <Plus size={13} />
              Add Task
            </button>
          )}
          {selectedMilestoneId === 'all' && (
            <p className="text-xs text-gray-400 italic">Select a milestone to add tasks</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Trash toggle */}
          {hasTrash && (
            <button
              onClick={() => setShowTrash(v => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border
                ${showTrash
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-red-600 border-red-200 hover:bg-red-50'}`}
            >
              <Trash2 size={13} />
              Trash ({columns.trash.length})
            </button>
          )}
          {/* Clear trash */}
          {showTrash && hasTrash && (
            <button
              onClick={handleClearTrash}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-100 border border-red-200 transition-colors"
            >
              Clear All
            </button>
          )}
          <button
            onClick={() => setIsMilestoneModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-[#37559e] rounded-lg text-xs font-semibold hover:bg-blue-50 border border-blue-200 transition-colors shadow-sm"
          >
            <Plus size={13} />
            Milestone
          </button>
        </div>
      </div>

      {/* ── Board ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-3 p-3 h-full min-w-max">
            {COLUMN_ORDER.map(renderColumn)}

            {/* Trash column — shown only when toggled */}
            {showTrash && (
              <div className="flex flex-col flex-shrink-0 w-64 sm:w-72 rounded-xl border border-dashed border-red-200 bg-red-50/40 overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2.5 bg-white border-b border-red-200">
                  <Trash2 size={14} className="text-red-400" />
                  <span className="text-sm font-semibold text-gray-600">Trash</span>
                  <span className="px-1.5 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-600">
                    {columns.trash.length}
                  </span>
                </div>
                <StrictModeDroppable droppableId="trash" type="TASK">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-1 p-2 space-y-2 min-h-[220px] overflow-y-auto transition-colors
                        ${snapshot.isDraggingOver ? 'bg-red-50' : ''}`}
                    >
                      {columns.trash.length === 0 && !snapshot.isDraggingOver && (
                        <EmptyColumn columnId="trash" />
                      )}
                      {columns.trash.map((task, idx) => (
                        <TaskCard
                          key={`task-${String(task.id)}`}
                          task={task}
                          index={idx}
                          onClick={handleTaskClick}
                        />
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </StrictModeDroppable>
              </div>
            )}
          </div>
        </DragDropContext>
      </div>

      {/* ── Overall progress bar ──────────────────────────────────────────── */}
      <div className="flex-shrink-0 px-4 py-2 bg-white border-t border-gray-200 flex items-center gap-3">
        <span className="text-xs text-gray-500 font-medium whitespace-nowrap">
          Overall progress
        </span>
        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#37559e] rounded-full transition-all duration-500"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
        <span className="text-xs font-semibold text-[#37559e] whitespace-nowrap">
          {overallProgress}%
        </span>
      </div>

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      <TaskDetailsModal
        isOpen={isTaskDetailsModalOpen}
        onClose={() => { setIsTaskDetailsModalOpen(false); setCurrentTask(null); }}
        task={currentTask}
        onEdit={() => { setIsTaskDetailsModalOpen(false); setIsTaskModalOpen(true); }}
        projectId={projectId}
      />
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={() => { setIsTaskModalOpen(false); setCurrentTask(null); }}
        onSave={handleSaveTask}
        task={currentTask}
        projectId={projectId}
        teamMembers={projectTeamMembers}
      />
      <TaskReviewModal
        isOpen={isTaskReviewModalOpen}
        onClose={() => { setIsTaskReviewModalOpen(false); setCurrentTask(null); }}
        task={currentTask}
        currentUser={currentUser}
        onApprove={handleReviewAction}
        onReject={handleReviewAction}
        onUpdateTask={handleReviewAction}
        projectId={projectId}
        teamMembers={projectTeamMembers}
      />
      <MilestoneModal
        isOpen={isMilestoneModalOpen}
        onClose={() => setIsMilestoneModalOpen(false)}
        onSave={handleCreateMilestone}
        projectId={projectId}
        teamMembers={projectTeamMembers}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
      />

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default KanbanBoard;
